import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Divider
} from '@mui/material';
import { prestadorFetch } from '../../contexts/PrestadorAuthContext';
import { DOCS_PROPOSTA_EDITAL } from '../../constants/prestadorDocumentos';

export default function PrestadorPropostaEditalDialog({ open, onClose, edital, onEnviado }) {
  const [valor, setValor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [propostaId, setPropostaId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [erro, setErro] = useState('');
  const [passo, setPasso] = useState(1);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open || !edital?.codigo) return;
    setPasso(1);
    setErro('');
    prestadorFetch(`/api/prestador/editais/${encodeURIComponent(edital.codigo)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.proposta) {
          setPropostaId(data.proposta.id);
          setValor(data.proposta.valor != null ? String(data.proposta.valor) : '');
          setDescricao(data.proposta.descricao || '');
        }
      })
      .catch(() => {});
  }, [open, edital?.codigo]);

  const adicionarDoc = () => {
    setDocs((prev) => [...prev, { tipo: 'Proposta', file: null, key: Date.now() }]);
  };

  const criarProposta = async () => {
    setErro('');
    setSalvando(true);
    try {
      const res = await prestadorFetch('/api/prestador/propostas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          codigoEdital: edital.codigo,
          valor: valor ? Number(valor) : null,
          descricao
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao registrar proposta');
      setPropostaId(data.id);
      setPasso(2);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const enviarDocumentos = async () => {
    if (!propostaId) return;
    const comArquivo = docs.filter((d) => d.file);
    if (!comArquivo.length) {
      setErro('Anexe ao menos um documento da proposta.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const fd = new FormData();
      comArquivo.forEach((d) => fd.append('arquivos', d.file));
      fd.append('tiposDocumento', JSON.stringify(comArquivo.map((d) => d.tipo)));

      const res = await prestadorFetch(`/api/prestador/propostas/${propostaId}/documentos`, {
        method: 'POST',
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao enviar documentos');

      onEnviado?.();
      onClose();
      setDocs([]);
      setPasso(1);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Proposta — edital {edital?.codigo}
        {edital?.chamamento > 1 && <Chip size="small" label={`${edital.chamamento}º chamamento`} sx={{ ml: 1 }} />}
      </DialogTitle>
      <DialogContent dividers>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {edital?.titulo}
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
          Encerramento: {edital?.data_encerramento || '—'}
          {edital?.processo_codigo && ` · Processo ${edital.processo_codigo}`}
        </Typography>

        {passo === 1 && (
          <Stack spacing={2}>
            <TextField
              label="Valor proposto (R$)"
              type="number"
              size="small"
              fullWidth
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
            <TextField
              label="Descrição / observações"
              multiline
              minRows={3}
              fullWidth
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </Stack>
        )}

        {passo === 2 && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Proposta registrada. Anexe a documentação exigida (alimenta o fluxo de contratação no SGC).
            </Alert>
            <Typography variant="subtitle2" gutterBottom>Documentos da proposta</Typography>
            {docs.map((d, idx) => (
              <Box key={d.key} display="flex" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={d.tipo}
                    onChange={(e) =>
                      setDocs((prev) => prev.map((x, i) => (i === idx ? { ...x, tipo: e.target.value } : x)))
                    }
                  >
                    {DOCS_PROPOSTA_EDITAL.map((t) => (
                      <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none' }}>
                  {d.file ? d.file.name : 'Selecionar arquivo'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) =>
                      setDocs((prev) =>
                        prev.map((x, i) => (i === idx ? { ...x, file: e.target.files?.[0] || null } : x))
                      )
                    }
                  />
                </Button>
                <Button size="small" color="error" onClick={() => setDocs((prev) => prev.filter((_, i) => i !== idx))}>
                  Remover
                </Button>
              </Box>
            ))}
            <Button size="small" onClick={adicionarDoc} sx={{ textTransform: 'none', mt: 1 }}>
              + Adicionar arquivo
            </Button>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Tipos esperados: proposta, contrato social, CPF/RG dos sócios, certidões negativas e documentação técnica.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Fechar</Button>
        {passo === 1 && (
          <Button
            variant="contained"
            onClick={() => (propostaId ? setPasso(2) : criarProposta())}
            disabled={salvando}
            sx={{ textTransform: 'none' }}
          >
            {salvando ? 'Salvando...' : propostaId ? 'Continuar para documentos' : 'Registrar proposta'}
          </Button>
        )}
        {passo === 2 && (
          <Button variant="contained" onClick={enviarDocumentos} disabled={salvando} sx={{ textTransform: 'none' }}>
            {salvando ? 'Enviando...' : 'Enviar documentação'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
